import { Button, Chip } from "@legal-500/legal500-design-system";
import type { KeyspaceDirectoryEntry } from "@legal-500/redis-db-manager-types";
import type { DbManagerKeyspace } from "../../../api/dbManager.service.js";
import { CopyLabelButton } from "./CopyLabelButton.js";
import { SlotProgressBar } from "./SlotProgressBar.js";
import { StatusBadge } from "./StatusBadge.js";
import { UserLabel } from "./UserLabel.js";
import { NON_CORE_SYNC_DISABLED_TOOLTIP, isCustomRelease } from "./releaseDefinitionUtils.js";
import { SearchIndexLine } from "./SearchIndexLine.js";
import { type MakeActiveButtonState, makeActiveButtonState } from "./searchIndexStatus.js";
import {
  computeSlotStatus,
  formatDurationMs,
  formatTimestamp,
  humanizeBytes,
  splitLabel,
} from "./slotStatus.js";
import styles from "./DbManager.module.css";

export type SlotCardActions = {
  onTriggerSync: (keyspace: string) => void;
  onMakeActive: (keyspace: string) => void;
  onPreviewLink: (keyspace: string) => void;
  onDiscard: (keyspace: string) => void;
  onRemove: (keyspace: string) => void;
  onViewProgress: (keyspace: string) => void;
  onViewDetail: (keyspace: string) => void;
  onViewFailures: (keyspace: string) => void;
  onRetrySync: (keyspace: string) => void;
  onCancelSync: (keyspace: string) => void;
};

type SlotCardProps = {
  entry: DbManagerKeyspace;
  isActive: boolean;
  allPageTypes: string[];
  memory?: { keyCount: number; datasetBytes: number };
  actions: SlotCardActions;
};

export function SlotCard({ entry, isActive, allPageTypes, memory, actions }: SlotCardProps) {
  const { status } = computeSlotStatus(entry, isActive, allPageTypes);
  const { prefix, suffix } = splitLabel(entry.keyspace || "");
  const docKeyCount = entry.totals?.docKeyCount ?? 0;
  const totalDuration = formatDurationMs(entry.totals?.durationMs);
  const isCustom = isCustomRelease(entry.releaseDefinition);
  const makeActive = makeActiveButtonState(entry);

  return (
    <div
      className={`${styles.card} ${isActive ? styles.cardActive : ""} ${
        status === "running" ? styles.cardRunning : ""
      } ${isCustom ? styles.cardReleaseApplied : ""}`}
      data-testid={`slot-card-${entry.keyspace}`}
      data-status={status}
      data-release-applied={isCustom ? "true" : undefined}
    >
      {status === "running" && (
        <SlotProgressBar keyspace={entry.keyspace} phase={entry.phase ?? null} />
      )}
      <div className={styles.cardHeader}>
        <div className={styles.slotNumber}>
          <code>{entry.keyspace}</code>
        </div>
        <div className={styles.cardHeaderBadges}>
          {isCustom && (
            <Chip size="small" color="warning" variant="filled">
              Custom Release
            </Chip>
          )}
          <StatusBadge status={status} />
        </div>
      </div>

      <div className={styles.label} title={entry.keyspace}>
        <>
          <span className={styles.labelPrefix}>{prefix}</span>
          <span className={styles.labelSuffix}>{suffix}</span>
          <CopyLabelButton value={entry.keyspace} />
        </>
      </div>

      <div className={styles.cardMeta}>
        <span>Synced: {formatTimestamp(entry.finishedAt ?? entry.startedAt)}</span>
        <span>
          Triggered by: <UserLabel value={entry.triggeredBy} />
        </span>
        <span>Doc keys: {docKeyCount.toLocaleString()}</span>
        <span>Duration: {totalDuration}</span>
        {memory && <span>Redis: {memory.keyCount.toLocaleString()} keys</span>}
        {memory && <span>Size: {humanizeBytes(memory.datasetBytes)}</span>}
        <SearchIndexLine entry={entry} />
      </div>

      <div className={styles.cardActions}>
        <div className={styles.cardActionDetails}>
          <Button
            variant="text"
            color="primary"
            size="small"
            onClick={() => actions.onViewDetail(entry.keyspace)}
          >
            Details
          </Button>
        </div>
        <div className={styles.cardActionPrimary}>
          {renderPrimary(status, entry.keyspace, isActive, actions, entry.family)}
        </div>
        <div className={styles.cardActionSecondary}>
          {renderSecondary(status, entry.keyspace, isActive, actions, makeActive)}
        </div>
        <div className={styles.cardActionTertiary}>
          {renderTertiary(status, entry.keyspace, isActive, actions)}
        </div>
        <div className={styles.cardActionQuaternary}>
          {renderQuaternary(status, entry.keyspace, isActive, actions)}
        </div>
      </div>
    </div>
  );
}

function renderPrimary(
  status: ReturnType<typeof computeSlotStatus>["status"],
  keyspace: string,
  isActive: boolean,
  actions: SlotCardActions,
  family: KeyspaceDirectoryEntry["family"],
) {
  if (isActive) return null;
  if (status === "running") {
    return (
      <Button
        variant="contained"
        color="primary"
        size="small"
        onClick={() => actions.onViewProgress(keyspace)}
      >
        View progress
      </Button>
    );
  }
  if (status === "failed") {
    return (
      <Button
        variant="contained"
        color="primary"
        size="small"
        onClick={() => actions.onRetrySync(keyspace)}
      >
        Retry sync
      </Button>
    );
  }
  const isNonCoreFamily = family !== "core";
  return (
    <Button
      variant="contained"
      color="primary"
      size="small"
      onClick={() => actions.onTriggerSync(keyspace)}
      disabled={isNonCoreFamily}
      title={isNonCoreFamily ? NON_CORE_SYNC_DISABLED_TOOLTIP : undefined}
    >
      Trigger sync
    </Button>
  );
}

function renderSecondary(
  status: ReturnType<typeof computeSlotStatus>["status"],
  keyspace: string,
  isActive: boolean,
  actions: SlotCardActions,
  makeActive: MakeActiveButtonState,
) {
  if (isActive) return null;
  if (status === "running") {
    return (
      <Button
        variant="outlined"
        color="error"
        size="small"
        onClick={() => actions.onCancelSync(keyspace)}
      >
        Cancel sync
      </Button>
    );
  }
  if (status === "failed") {
    return (
      <Button
        variant="outlined"
        color="default"
        size="small"
        onClick={() => actions.onViewFailures(keyspace)}
      >
        View failures
      </Button>
    );
  }
  if (status === "empty") return null;
  return (
    <Button
      variant="outlined"
      color="primary"
      size="small"
      onClick={() => actions.onMakeActive(keyspace)}
      disabled={makeActive.disabled}
      title={makeActive.title}
      aria-describedby={makeActive.describedById}
    >
      Make active
    </Button>
  );
}

function renderTertiary(
  status: ReturnType<typeof computeSlotStatus>["status"],
  keyspace: string,
  isActive: boolean,
  actions: SlotCardActions,
) {
  if (isActive) return null;
  if (status === "failed") {
    return (
      <Button
        variant="outlined"
        color="error"
        size="small"
        onClick={() => actions.onDiscard(keyspace)}
      >
        Discard
      </Button>
    );
  }
  if (status === "empty" || status === "running") return null;
  return (
    <Button
      variant="outlined"
      color="default"
      size="small"
      onClick={() => actions.onPreviewLink(keyspace)}
    >
      Generate preview link
    </Button>
  );
}

function renderQuaternary(
  status: ReturnType<typeof computeSlotStatus>["status"],
  keyspace: string,
  isActive: boolean,
  actions: SlotCardActions,
) {
  if (isActive || status === "running") return null;
  return (
    <Button variant="text" color="error" size="small" onClick={() => actions.onRemove(keyspace)}>
      Remove
    </Button>
  );
}
